import React, { useEffect, useMemo, useState } from "react";
import { Column, useSortBy, useTable } from "react-table";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import MemberServices from "../../services/MemberServices";
import PositionServices from "../../services/PositionServices";
import { Button, Label } from "../../components/StyledComp/StyledComp";
import "../../Styles/CommonStyles.scss";

interface ISection {
  Id: number;
  SectionName: string;
}

interface IPosition {
  Id: number;
  SectionId: number;
  SectionName: string;
  Name: string;
  Status: number;
}

const PositionsPage: React.FC = () => {
  const [sections, setSections] = useState<ISection[]>([]);
  const [selectedSectionId, setSelectedSectionId] = useState<number | null>(null);
  const [positions, setPositions] = useState<IPosition[]>([]);
  const [newPositionName, setNewPositionName] = useState<string>("");
  const [editingPosition, setEditingPosition] = useState<IPosition | null>(null);
  const [editName, setEditName] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    const loadSections = async () => {
      try {
        const response = await MemberServices.getConfigurationList();
        if (response.data?.data?.MemberSection) {
          setSections(response.data.data.MemberSection);
        }
      } catch (err) {
        alert("Failed to load sections.");
      }
    };
    loadSections();
  }, []);

  const loadPositions = async (sectionId: number) => {
    setLoading(true);
    try {
      const response = await PositionServices.getPositions(sectionId);
      if (response.data?.data != null) {
        setPositions(response.data.data);
      } else {
        setPositions([]);
      }
    } catch (err) {
      alert("Failed to load positions.");
    } finally {
      setLoading(false);
    }
  };

  const handleSectionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = parseInt(e.target.value, 10);
    setSelectedSectionId(id || null);
    setNewPositionName("");
    setEditingPosition(null);
    if (id) loadPositions(id);
    else setPositions([]);
  };

  const handleAddPosition = async () => {
    if (!selectedSectionId || !newPositionName.trim()) return;
    try {
      const response = await PositionServices.createPosition({
        SectionId: selectedSectionId,
        Name: newPositionName.trim(),
      });
      if (response.data?.data != null) {
        setNewPositionName("");
        loadPositions(selectedSectionId);
      } else {
        alert(response.data?.errors?.[0]?.message || "Failed to create position.");
      }
    } catch (err) {
      alert("Failed to create position.");
    }
  };

  const handleDeletePosition = async (position: IPosition) => {
    if (!window.confirm(`Delete position "${position.Name}"? This cannot be done if members are currently assigned to it.`)) return;
    try {
      const response = await PositionServices.deletePosition(position.Id);
      if (response.data?.status) {
        loadPositions(selectedSectionId!);
      } else {
        const msg = response.data?.errors?.[0]?.message || "Failed to delete position.";
        alert(msg);
      }
    } catch (err: any) {
      const msg = err?.response?.data?.errors?.[0]?.message || "Failed to delete position.";
      alert(msg);
    }
  };

  const handleStartEdit = (position: IPosition) => {
    setEditingPosition(position);
    setEditName(position.Name);
  };

  const handleSaveEdit = async () => {
    if (!editingPosition || !editName.trim()) return;
    try {
      const response = await PositionServices.updatePosition(editingPosition.Id, {
        Name: editName.trim(),
      });
      if (response.data?.status) {
        setEditingPosition(null);
        loadPositions(selectedSectionId!);
      } else {
        alert("Failed to update position.");
      }
    } catch (err) {
      alert("Failed to update position.");
    }
  };

  const columns = useMemo<Column<IPosition>[]>(
    () => [
      { id: "Name", Header: "Position Name", accessor: "Name" },
      {
        id: "actions",
        Header: "Actions",
        accessor: (row: IPosition) => row,
        Cell: ({ value: position }: { value: IPosition }) => (
          <div style={{ display: "flex", gap: "12px" }}>
            <a
              style={{ cursor: "pointer", color: "#1976d2" }}
              onClick={() => handleStartEdit(position)}
            >
              <EditIcon fontSize="small" />
            </a>
            <a
              style={{ cursor: "pointer", color: "#d32f2f" }}
              onClick={() => handleDeletePosition(position)}
            >
              <DeleteIcon fontSize="small" />
            </a>
          </div>
        ),
      },
    ],
    [selectedSectionId]
  );

  const tableData = useMemo(() => positions, [positions]);

  const tableInstance = useTable({ columns, data: tableData }, useSortBy);
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    tableInstance;

  const selectedSectionName = sections.find((s) => s.Id === selectedSectionId)?.SectionName;

  return (
    <div className="dashboard-wrapper" style={{ padding: "24px", alignItems: "flex-start" }}>
      <h1 className="page-heading">Section Positions</h1>
      <p style={{ marginBottom: "20px", color: "#555" }}>
        Create and manage officer positions for each section. Once created, positions
        can be assigned to members from the member detail page.
      </p>

      {/* Section selector */}
      <div style={{ marginBottom: "24px", maxWidth: "400px" }}>
        <Label>Select Section</Label>
        <select
          className="input-field-div"
          style={{ width: "100%", marginTop: "6px" }}
          value={selectedSectionId ?? ""}
          onChange={handleSectionChange}
        >
          <option value="">-- Select a Section --</option>
          {sections.map((section) => (
            <option key={section.Id} value={section.Id}>
              {section.SectionName}
            </option>
          ))}
        </select>
      </div>

      {selectedSectionId && (
        <>
          <h2 style={{ fontSize: "1.1rem", fontWeight: 600, marginBottom: "12px" }}>
            Positions for: {selectedSectionName}
          </h2>

          {/* Add position form */}
          <div
            style={{
              display: "flex",
              gap: "10px",
              alignItems: "center",
              marginBottom: "20px",
              maxWidth: "500px",
              width: "100%",
              flexDirection: "column",
            }}
          >
            <input
              className="input-field-div"
              style={{ flex: 1 }}
              placeholder="New position name (e.g. Vice-Chair (Consumer))"
              value={newPositionName}
              onChange={(e) => setNewPositionName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleAddPosition(); }}
            />
            <Button
              type="button"
              onClick={handleAddPosition}
              disabled={!newPositionName.trim()}
            >
              Add Position
            </Button>
          </div>

          {/* Inline edit form */}
          {editingPosition && (
            <div
              style={{
                display: "flex",
                gap: "10px",
                alignItems: "center",
                marginBottom: "20px",
                maxWidth: "500px",
                width: "100%",
                padding: "12px",
                background: "#f5f5f5",
                borderRadius: "6px",
                flexDirection: "column",
              }}
            >
              <span style={{ whiteSpace: "nowrap", fontSize: "0.9rem" }}>Editing:</span>
              <input
                className="input-field-div"
                style={{ flex: 1 }}
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleSaveEdit(); }}
                autoFocus
              />
              <Button type="button" onClick={handleSaveEdit} disabled={!editName.trim()}>
                Save
              </Button>
              <button
                type="button"
                onClick={() => setEditingPosition(null)}
                style={{ background: "none", border: "none", cursor: "pointer", color: "#555" }}
              >
                Cancel
              </button>
            </div>
          )}

          {/* Positions table */}
          {loading ? (
            <p>Loading...</p>
          ) : positions.length === 0 ? (
            <p style={{ color: "#888" }}>No positions yet. Add one above.</p>
          ) : (
            <table {...getTableProps()} className="table" style={{ maxWidth: "600px" }}>
              <thead>
                {headerGroups.map((headerGroup) => (
                  <tr {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => (
                      <th
                        {...column.getHeaderProps((column as any).getSortByToggleProps())}
                        style={{ textAlign: "left", padding: "8px 12px", borderBottom: "2px solid #ddd" }}
                      >
                        {column.render("Header")}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()}>
                {rows.map((row) => {
                  prepareRow(row);
                  return (
                    <tr {...row.getRowProps()} style={{ borderBottom: "1px solid #eee" }}>
                      {row.cells.map((cell) => (
                        <td
                          {...cell.getCellProps()}
                          style={{ padding: "8px 12px" }}
                        >
                          {cell.render("Cell")}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
};

export default PositionsPage;
